import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Stack,
  Tooltip,
  Avatar,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import PersonRemoveIcon from '@mui/icons-material/PersonRemove'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import MedicalServicesIcon from '@mui/icons-material/MedicalServices'
import GroupIcon from '@mui/icons-material/Group'
import dayjs from 'dayjs'
import { EligibleStaffResponse } from '@/core/models/planning.model'
import { Shift } from '@/core/models/shift.model'
import { StaffSelector } from './StaffSelector'
import { Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'

type ShiftDialogProps = {
  open: boolean
  onClose: () => void
  shift: Shift
  onRemoveStaff: (staffId: string) => void
  eligibleStaff?: EligibleStaffResponse[]
  onAssignEligibleStaff?: (staffIds: string[], cb?: () => void) => void
  isLoadingEligibleStaff?: boolean
}

export function ShiftDialog({
  open,
  onClose,
  shift,
  onRemoveStaff,
  eligibleStaff = [],
  onAssignEligibleStaff,
  isLoadingEligibleStaff = false,
}: ShiftDialogProps) {
  useLingui()
  const { t } = useTranslation()
  const [selectedStaffIds, setSelectedStaffIds] = useState<string[]>([])
  const isMobile = useIsMobile()

  if (!shift) return null

  // Calculate staffing status
  const totalStaffRequired = shift.shiftTemplate?.requiredStaffCount || 0
  const assignedStaffCount = (shift.assignedStaff || []).length
  const remainingSpots = totalStaffRequired - assignedStaffCount
  const canAddMoreStaff = remainingSpots > 0

  // Get status chip props
  const getStatusChip = () => {
    if (dayjs(shift.endTime).isBefore(dayjs())) {
      return { text: t('page.planning.shift.status.past'), color: 'default' }
    }

    if (remainingSpots <= 0) {
      return { text: t('page.planning.shift.status.staffed'), color: 'success' }
    }

    if (assignedStaffCount > 0) {
      return {
        text: t('page.planning.shift.status.partiallyStaffed', {
          count: remainingSpots,
        }),
        color: 'warning',
      }
    }

    return { text: t('page.planning.shift.status.empty'), color: 'error' }
  }

  const statusChip = getStatusChip()

  const handleConfirm = () => {
    onAssignEligibleStaff?.(selectedStaffIds, () => {
      setSelectedStaffIds([])
    })
  }

  // Get already assigned staff IDs
  const alreadyAssignedStaffIds = (shift.assignedStaff || []).map((staff) => staff._id)

  return (
    <Dialog open={open} onClose={onClose} fullScreen={isMobile} fullWidth maxWidth="sm">
      <DialogTitle>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="h6">{t('page.planning.shift.details.title')}</Typography>
          <Chip label={statusChip.text} color={statusChip.color as any} size="small" />
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={1} sx={{ mb: 3 }}>
          {shift.shiftTemplate?.location && (
            <Typography display="flex" alignItems="center" variant="body2" color="text.secondary">
              <LocationOnIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
              {shift.shiftTemplate.location}
            </Typography>
          )}
          {shift.shiftTemplate?.consultationReason && (
            <Typography display="flex" alignItems="center" variant="body2" color="text.secondary">
              <MedicalServicesIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
              {shift.shiftTemplate.consultationReason}
            </Typography>
          )}

          <Typography display="flex" alignItems="center" variant="body2" color="text.secondary">
            <GroupIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
            <Trans>
              {assignedStaffCount} / {totalStaffRequired} Staff selected
            </Trans>
          </Typography>
          <Typography display="flex" variant="body2" color="text.secondary">
            <AccessTimeIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
            {dayjs(shift.startTime).format('MMM D, YYYY h:mm A')} - {dayjs(shift.endTime).format('h:mm A')}
          </Typography>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle1">{t('page.planning.shift.details.assignedStaff')}</Typography>
        </Stack>

        {(shift.assignedStaff || []).length > 0 ? (
          <List>
            {(shift.assignedStaff || []).map((staff) => (
              <ListItem key={staff._id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor: 'primary.main',
                      width: 32,
                      height: 32,
                    }}
                  >
                    {staff.userInfo?.name?.charAt(0) || ''}
                    {staff.userInfo?.surname?.charAt(0) || ''}
                  </Avatar>
                  <ListItemText
                    primary={`${staff.userInfo?.name || ''} ${staff.userInfo?.surname || ''}`}
                    secondary={staff.speciality || t('page.planning.shift.details.staff')}
                  />
                </Box>
                <ListItemSecondaryAction>
                  <Tooltip title={t('page.planning.shift.details.removeStaff')}>
                    <IconButton edge="end" size="small" onClick={() => onRemoveStaff(staff._id)}>
                      <PersonRemoveIcon />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
            {t('page.planning.shift.details.noStaff')}
          </Typography>
        )}

        {canAddMoreStaff && onAssignEligibleStaff && (
          <>
            <Divider sx={{ my: 2 }} />
            <StaffSelector
              eligibleStaffs={eligibleStaff}
              requiredStaffCount={remainingSpots}
              selectedStaffIds={selectedStaffIds}
              onStaffSelected={setSelectedStaffIds}
              isLoading={isLoadingEligibleStaff}
              isMultipleStaffRequired={totalStaffRequired > 1}
              alreadyAssignedStaffIds={alreadyAssignedStaffIds}
            />
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          <Trans>Close</Trans>
        </Button>
        <Button onClick={handleConfirm} color="primary" variant="contained" disabled={!selectedStaffIds.length}>
          <Trans>Confirm</Trans>
        </Button>
      </DialogActions>
    </Dialog>
  )
}
